import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { useAuth } from '../context/AuthContext';

/* ═══════════════════════════════════════════════════════════════════════════
   SUPPLY CHAIN RING — Animated ring showing network health
   ═══════════════════════════════════════════════════════════════════════════ */
function SupplyChainRing({ targetScore = 94, animate = false }: { targetScore?: number; animate?: boolean }) {
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let frame: number;
    const duration = 2500, start = performance.now();
    const tick = (now: number) => { const p = Math.min((now - start) / duration, 1); setDisplayScore(Math.round((1 - Math.pow(1 - p, 3)) * targetScore)); if (p < 1) frame = requestAnimationFrame(tick); };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);
  const c = 2 * Math.PI * 90;
  return (
    <div className="supply-ring">
      <svg width="220" height="220" viewBox="0 0 200 200">
        <defs><linearGradient id="linkGradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#f59e0b" /><stop offset="50%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#fde68a" /></linearGradient></defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle className="ring-fill" cx="100" cy="100" r="90" style={{ strokeDasharray: c, strokeDashoffset: animate ? c - (c * targetScore) / 100 : c, transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
      </svg>
      <div className="supply-ring-value">
        <div className="stat-counter" style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff' }}>{displayScore}%</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(251, 191, 36, 0.7)', fontWeight: 500, marginTop: 4 }}>Network Resilience</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUPPLY CHAIN DEMO — Live risk detection across nodes
   ═══════════════════════════════════════════════════════════════════════════ */
function SupplyChainDemo() {
  const [phase, setPhase] = useState<'idle' | 'scanning' | 'risk' | 'resolved'>('idle');
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('scanning');
    setTimeout(() => setPhase('risk'), 2000);
    setTimeout(() => setPhase('resolved'), 4500);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setTimeout(startDemo, 400); }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startDemo]);

  const nodes = [
    { id: 1, name: 'Raw Materials', emoji: '🏭', status: phase === 'risk' ? 'warning' : 'ok', location: 'Shenzhen, China' },
    { id: 2, name: 'Manufacturing', emoji: '⚙️', status: 'ok', location: 'Munich, Germany' },
    { id: 3, name: 'Logistics Hub', emoji: '🚢', status: phase === 'risk' ? 'critical' : phase === 'resolved' ? 'rerouted' : 'ok', location: 'Rotterdam, NL' },
    { id: 4, name: 'Distribution', emoji: '📦', status: 'ok', location: 'London, UK' },
  ];

  const statusColor = (s: string) => s === 'critical' ? '#ef4444' : s === 'warning' ? '#f59e0b' : s === 'rerouted' ? '#3b82f6' : '#10b981';
  const statusLabel = (s: string) => s === 'critical' ? '⚠️ DISRUPTED' : s === 'warning' ? '⚡ AT RISK' : s === 'rerouted' ? '🔄 REROUTED' : '✅ NOMINAL';

  return (
    <div ref={ref} style={{ width: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div className="glass-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: '1px solid rgba(245,158,11,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase === 'resolved' ? '#10b981' : phase === 'risk' ? '#ef4444' : '#f59e0b' }} />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500 }}>
            {phase === 'idle' ? 'Supply Chain Monitor' : phase === 'scanning' ? '🔍 Scanning 847 supply nodes...' : phase === 'risk' ? '🚨 Disruption Detected — AI Rerouting...' : '✅ AI Rerouted — Zero Impact'}
          </span>
        </div>

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {nodes.map((node, idx) => (
            <div key={node.id} className="supply-node" style={{ animationDelay: `${idx * 0.15}s`, display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 12, background: 'rgba(17, 22, 56, 0.4)', border: `1px solid ${node.status === 'critical' ? 'rgba(239,68,68,0.3)' : node.status === 'warning' ? 'rgba(245,158,11,0.3)' : node.status === 'rerouted' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.1)'}` }}>
              <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>{node.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>{node.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{node.location}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(node.status), boxShadow: `0 0 8px ${statusColor(node.status)}` }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: statusColor(node.status), textTransform: 'uppercase', letterSpacing: 0.5 }}>{statusLabel(node.status)}</span>
              </div>
              {idx < nodes.length - 1 && <div style={{ position: 'absolute', right: -20, fontSize: '1.2rem', color: 'rgba(245,158,11,0.4)' }}>→</div>}
            </div>
          ))}
        </div>

        {phase === 'resolved' && (
          <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', fontSize: '0.85rem', color: '#34d399' }}>
            🤖 <strong>AI Decision:</strong> Rerouted via Hamburg logistics hub. Delivery impact: 0 days. Cost delta: +£120 (vs £45,000 disruption cost avoided).
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DEMAND FORECAST MINI CHART
   ═══════════════════════════════════════════════════════════════════════════ */
function DemandForecastDemo() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => { const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.3 }); if (ref.current) o.observe(ref.current); return () => o.disconnect(); }, []);

  const actual = [42, 58, 51, 67, 73, 62, 78, 85];
  const predicted = [40, 55, 54, 65, 70, 68, 80, 88, 92, 97];
  const max = 100;

  return (
    <div ref={ref} className="glass-card" style={{ padding: '1.5rem', maxWidth: 500, margin: '0 auto' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>AI Demand Forecast — SKU-4821</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
        {actual.map((v, i) => (
          <div key={`a-${i}`} style={{ flex: 1, height: inView ? `${(v / max) * 100}%` : '0%', background: 'rgba(245,158,11,0.5)', borderRadius: '4px 4px 0 0', transition: `height 0.8s ease ${i * 0.08}s` }} />
        ))}
        {predicted.slice(actual.length).map((v, i) => (
          <div key={`p-${i}`} style={{ flex: 1, height: inView ? `${(v / max) * 100}%` : '0%', background: 'rgba(245,158,11,0.15)', border: '1px dashed rgba(245,158,11,0.4)', borderRadius: '4px 4px 0 0', transition: `height 0.8s ease ${(actual.length + i) * 0.08}s` }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.7rem', color: '#64748b' }}>
        <span>← Actual</span><span style={{ color: '#fbbf24' }}>Predicted →</span>
      </div>
      <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#94a3b8' }}>🎯 <strong style={{ color: '#34d399' }}>94.7% accuracy</strong> — AI predicts demand surge in 2 weeks. Recommend +15% inventory buffer.</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STATS COUNTER
   ═══════════════════════════════════════════════════════════════════════════ */
function StatsCounter({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => { const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.5 }); if (ref.current) o.observe(ref.current); return () => o.disconnect(); }, []);
  useEffect(() => { if (!inView) return; let f: number; const d = 2000, s = performance.now(); const t = (n: number) => { const p = Math.min((n - s) / d, 1); setCount(Math.round((1 - Math.pow(1 - p, 3)) * end)); if (p < 1) f = requestAnimationFrame(t); }; f = requestAnimationFrame(t); return () => cancelAnimationFrame(f); }, [inView, end]);
  return <span ref={ref} className="stat-counter">{prefix}{count.toLocaleString()}{suffix}</span>;
}

const FEATURES = [
  { icon: '🔗', title: 'Digital Twin', description: 'Complete digital replica of your supply chain. Simulate disruptions, test scenarios, and optimize routes before they happen.', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)' },
  { icon: '🧠', title: 'AI Risk Intelligence', description: 'Monitor 50+ risk signals per supplier: geopolitical, weather, financial health, shipping congestion, and more.', gradient: 'linear-gradient(135deg, #ef4444, #f87171)' },
  { icon: '📊', title: 'Demand Forecasting', description: 'ML-powered demand prediction with 94.7% accuracy. Reduce overstock by 30% and stockouts by 85%.', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { icon: '🚢', title: 'Logistics Orchestration', description: 'Real-time shipment tracking, automated carrier selection, and AI-driven route optimization across global networks.', gradient: 'linear-gradient(135deg, #3b82f6, #60a5fa)' },
  { icon: '🤝', title: 'Supplier Portal', description: 'Collaborative platform for supplier onboarding, performance scorecards, compliance tracking, and automated PO management.', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { icon: '🌱', title: 'Sustainability Tracker', description: 'Scope 1-3 carbon mapping across your supply chain. ESG compliance dashboards and sustainability reporting.', gradient: 'linear-gradient(135deg, #14b8a6, #2dd4bf)' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN LANDING
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EFLinkLanding() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, ssoError } = useAuth();
  useEffect(() => {
    if (!loading && isAuthenticated && !ssoError) navigate('/dashboard', { replace: true });
  }, [loading, isAuthenticated, ssoError, navigate]);
  const [scrollY, setScrollY] = useState(0);
  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => { const h = () => setScrollY(window.scrollY); window.addEventListener('scroll', h, { passive: true }); return () => window.removeEventListener('scroll', h); }, []);
  useEffect(() => { const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeroInView(true); }, { threshold: 0.1 }); if (heroRef.current) o.observe(heroRef.current); return () => o.disconnect(); }, []);

  return (
    <div className="landing-root">
      {ssoError && <div role="alert" style={{ position: 'fixed', top: 76, zIndex: 100, background: '#7f1d1d', color: 'white', padding: 16 }}>{ssoError}</div>}
      {/* NAV */}
      <nav className={scrollY > 50 ? 'nav-glass' : ''} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, transition: 'all 0.3s ease', ...(scrollY <= 50 ? { background: 'transparent' } : {}) }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🔗</div>
            <div><span style={{ fontSize: '1.1rem', fontWeight: 700 }}>EF-Link</span><span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 8 }}>by Executive Force AI</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 16px', fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <button onClick={() => navigate('/login')} style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: '#f59e0b', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Start Free Trial</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section ref={heroRef} className="grid-pattern" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 1.5rem 3rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--1" /><div className="landing-orb landing-orb--2" /><div className="landing-orb landing-orb--3" /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.2s', marginBottom: 24 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>🔗 Adaptive Supply Chain Intelligence</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease 0.4s' }}>
            <span style={{ color: '#fff' }}>Supply Chains That</span><br /><span className="text-gradient-link">Think Ahead</span>
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)', color: '#94a3b8', maxWidth: 620, margin: '0 auto 3rem', lineHeight: 1.6, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 0.6s' }}>
            AI-powered supply chain intelligence. Predict disruptions. Reroute automatically. Turn your supply chain from a <span style={{ color: '#fde68a', fontWeight: 600 }}>liability into an advantage</span>.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48, opacity: heroInView ? 1 : 0, transform: heroInView ? 'scale(1)' : 'scale(0.8)', transition: 'all 0.8s ease 0.8s' }}>
            <div className="animate-float"><SupplyChainRing targetScore={94} animate={heroInView} /></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease 1.0s' }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial 🚀</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* SUPPLY CHAIN DEMO */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 16 }}>Live Risk Detection</span>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Watch AI <span className="text-gradient-link">Reroute in Real-Time</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>When disruptions hit, EF-Link's AI finds alternative routes automatically — before your operations notice.</p>
          </div>
          <SupplyChainDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* DEMAND FORECAST */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Predict Demand <span className="text-gradient-link">Before It Happens</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>ML-powered demand forecasting with 94.7% accuracy. Stop guessing inventory levels.</p>
          </div>
          <DemandForecastDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* FEATURES */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Enterprise-Grade <span className="text-gradient-link">Supply Intelligence</span></h2>
            <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: 600, margin: '0 auto' }}>Every tool you need to build a resilient, adaptive, and sustainable supply chain.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card" style={{ padding: '1.5rem' }}>
                <div className="feature-icon-glow"><div style={{ width: 48, height: 48, borderRadius: 12, background: f.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: 16 }}>{f.icon}</div></div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: '0.9rem', color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* COMPETITOR TABLE */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 48 }}>How EF-Link <span className="text-gradient-link">Compares</span></h2>
          <div className="glass-card" style={{ padding: '2rem', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(245,158,11,0.2)' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#94a3b8' }}>Feature</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#fbbf24', fontWeight: 700 }}>EF-Link</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>SAP SCM</th>
                <th style={{ padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>Oracle SCM</th>
              </tr></thead>
              <tbody>
                {[
                  ['AI-Native', '✅', '❌', '❌'],
                  ['Digital Twin', '✅', '💰💰', '💰💰'],
                  ['Auto-Rerouting', '✅', '❌', '❌'],
                  ['Demand Forecasting', '✅', '✅', '✅'],
                  ['Sustainability/ESG', '✅', '💰', '💰'],
                  ['Setup Time', '1 Day', '6-18 Months', '6-12 Months'],
                  ['Price', '£29/mo', '£50K+/yr', '£40K+/yr'],
                ].map(([f, ...v]) => (
                  <tr key={f} style={{ borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
                    <td style={{ padding: '12px 8px', color: '#e2e8f0', textAlign: 'left' }}>{f}</td>
                    {v.map((val, i) => <td key={i} style={{ padding: '12px 8px', textAlign: 'center', color: i === 0 ? '#fff' : '#94a3b8' }}>{val}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* STATS */}
      <section style={{ position: 'relative', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 32, textAlign: 'center' }}>
          {[{ value: 847, suffix: '+', label: 'Supply Nodes Monitored' }, { value: 94, suffix: '%', label: 'Disruption Avoidance' }, { value: 30, suffix: '%', label: 'Cost Reduction' }, { value: 3, suffix: 'sec', label: 'Avg Reroute Time' }].map((s) => (
            <div key={s.label}><div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 4 }}><StatsCounter end={s.value} suffix={s.suffix} /></div><div style={{ fontSize: '0.85rem', color: '#64748b' }}>{s.label}</div></div>
          ))}
        </div>
      </section>

      <div className="section-divider" />

      {/* PRICING */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>Simple <span className="text-gradient-link">Pricing</span></h2>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', marginBottom: 40 }}>7-day free trial. No credit card required. Cancel anytime.</p>
          <div className="glass-card" style={{ padding: '2.5rem', borderColor: 'rgba(245,158,11,0.4)' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>EF-Link</div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: '3.5rem', fontWeight: 800, color: '#fff' }}>£29</span><span style={{ fontSize: '1.1rem', color: '#64748b' }}>/month</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 24 }}>Full Supply Chain Intelligence Platform</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', textAlign: 'left' }}>
              {['Digital twin of your supply chain', 'AI risk detection & auto-rerouting', 'ML demand forecasting (94.7% accuracy)', 'Supplier portal & scorecards', 'Sustainability & ESG tracking', 'Real-time logistics orchestration'].map((item) => (
                <li key={item} style={{ fontSize: '0.9rem', color: '#cbd5e1', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: '#fbbf24' }}>✓</span> {item}</li>
              ))}
            </ul>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start 7-Day Free Trial</button>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* FINAL CTA */}
      <section style={{ position: 'relative', padding: '6rem 1.5rem' }}>
        <div className="landing-orbs"><div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} /><div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} /></div>
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, marginBottom: 24 }}>Build a Supply Chain That <span className="text-gradient-link">Never Breaks</span></h2>
          <p style={{ fontSize: '1.2rem', color: '#94a3b8', marginBottom: 40 }}>Join companies using AI to predict, prevent, and profit from supply chain disruptions.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
            <button onClick={() => navigate('/login')} className="cta-glow" style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 600, color: '#fff', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: 12, cursor: 'pointer' }}>Start Free Trial →</button>
            <button onClick={() => navigate('/login')} style={{ padding: '16px 40px', fontSize: '1.1rem', fontWeight: 500, color: '#94a3b8', background: 'transparent', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, cursor: 'pointer' }}>Sign In</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: '1px solid rgba(245,158,11,0.15)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>🔗</div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>Executive Force AI</span>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#475569', margin: 0 }}>© {new Date().getFullYear()} Executive Force AI Limited. All rights reserved.</p>
          <div style={{ display: 'flex', gap: 24 }}>
            <button onClick={() => navigate('/login')} style={{ fontSize: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign In</button>
            <a href="https://executiveforceai.co.uk/privacy-policy" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Privacy</a>
            <a href="https://executiveforceai.co.uk/terms-conditions" style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'none' }}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
